import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Receipt, RefreshCw, Loader2, Search, Edit, Trash2, IndianRupee, 
  Calendar, CreditCard, Filter, Download, FileSpreadsheet, FileDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Transactions() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Edit modal
  const [editModal, setEditModal] = useState({ open: false, transaction: null });
  const [editData, setEditData] = useState({ amount: '', payment_method: '', reference_id: '', notes: '', transaction_date: '' });
  const [saving, setSaving] = useState(false);
  
  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ open: false, transaction: null });
  const [deleting, setDeleting] = useState(false);

  // Get unique months from transactions
  const getUniqueMonths = () => {
    const months = new Set();
    transactions.forEach(t => {
      if (t.month_label && t.month_label.trim()) {
        months.add(t.month_label);
      }
    });
    return Array.from(months).sort((a, b) => {
      // Sort by year then month
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
      return monthOrder.indexOf(monthB) - monthOrder.indexOf(monthA);
    });
  };

  // Load transactions
  const loadTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/all-transactions');
      setTransactions(res.data);
      filterTransactions(res.data, activeTab, searchTerm, selectedMonth, startDate, endDate);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, []);

  // Filter transactions
  const filterTransactions = (txns, tab, search, month, start, end) => {
    let filtered = [...txns];
    
    // Filter by type
    if (tab === 'payments') {
      filtered = filtered.filter(t => t.type === 'payment');
    } else if (tab === 'expenses') {
      filtered = filtered.filter(t => t.type === 'expense');
    }
    
    // Filter by month
    if (month && month !== 'all') {
      filtered = filtered.filter(t => t.month_label === month);
    }
    
    // Filter by date range
    if (start) {
      const startDate = new Date(start);
      filtered = filtered.filter(t => {
        const txnDate = new Date(t.date);
        return txnDate >= startDate;
      });
    }
    if (end) {
      const endDate = new Date(end);
      endDate.setHours(23, 59, 59); // Include the entire end date
      filtered = filtered.filter(t => {
        const txnDate = new Date(t.date);
        return txnDate <= endDate;
      });
    }
    
    // Filter by search term
    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter(t => 
        t.description?.toLowerCase().includes(term) ||
        t.entity_name?.toLowerCase().includes(term) ||
        t.entity_detail?.toLowerCase().includes(term) ||
        t.reference_id?.toLowerCase().includes(term)
      );
    }
    
    setFilteredTransactions(filtered);
  };

  useEffect(() => {
    filterTransactions(transactions, activeTab, searchTerm, selectedMonth, startDate, endDate);
  }, [activeTab, searchTerm, selectedMonth, startDate, endDate, transactions]);

  // Clear filters
  const clearFilters = () => {
    setSelectedMonth('all');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };

  // Open edit modal
  const openEditModal = (txn) => {
    setEditData({
      amount: txn.amount?.toString() || '',
      payment_method: txn.payment_method || 'cash',
      reference_id: txn.reference_id || '',
      notes: txn.notes || '',
      transaction_date: txn.date?.split('T')[0] || ''
    });
    setEditModal({ open: true, transaction: txn });
  };

  // Handle edit save
  const handleSaveEdit = async () => {
    const txn = editModal.transaction;
    if (!txn) return;
    
    setSaving(true);
    try {
      if (txn.type === 'payment') {
        await api.put(`/admin/transactions/${txn.id}`, {
          amount: parseFloat(editData.amount) || null,
          payment_method: editData.payment_method || null,
          reference_id: editData.reference_id || null,
          notes: editData.notes || null,
          transaction_date: editData.transaction_date || null
        });
      } else {
        await api.put(`/admin/expenses/${txn.id}`, {
          paid_amount: parseFloat(editData.amount) || null,
          payment_method: editData.payment_method || null,
          payment_reference: editData.reference_id || null,
          notes: editData.notes || null,
          payment_date: editData.transaction_date || null
        });
      }
      
      toast.success('Transaction updated successfully');
      setEditModal({ open: false, transaction: null });
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    const txn = deleteModal.transaction;
    if (!txn) return;
    
    setDeleting(true);
    try {
      if (txn.type === 'payment') {
        await api.delete(`/admin/transactions/${txn.id}`);
      } else {
        await api.put(`/admin/expenses/${txn.id}`, {
          paid_amount: 0,
          status: 'pending',
          payment_method: null,
          payment_reference: null,
          payment_date: null
        });
      }
      
      toast.success('Transaction deleted successfully');
      setDeleteModal({ open: false, transaction: null });
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Calculate totals
  const totals = {
    count: filteredTransactions.length,
    total: filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0),
    payments: filteredTransactions.filter(t => t.type === 'payment').reduce((sum, t) => sum + (t.amount || 0), 0),
    expenses: filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (t.amount || 0), 0)
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    
    // Header - Green bar with FreeCoffee branding
    doc.setFillColor(5, 150, 105);
    doc.rect(0, 0, pageWidth, 32, 'F');
    
    // Left side - Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Transaction Report', 12, 12);
    
    // Filter info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let filterText = activeTab === 'all' ? 'All Transactions' : activeTab === 'payments' ? 'Payment Receipts' : 'Expense Payments';
    if (selectedMonth !== 'all') filterText += ` | ${selectedMonth}`;
    if (startDate || endDate) filterText += ` | ${startDate || '...'} to ${endDate || '...'}`;
    doc.text(filterText, 12, 20);
    
    doc.setFontSize(9);
    doc.text(`Generated: ${generatedDate}`, 12, 27);
    
    // Right side - FreeCoffee branding
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('FreeCoffee', pageWidth - 12, 12, { align: 'right' });
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Your Free CommunityOS', pageWidth - 12, 19, { align: 'right' });
    doc.text('freecoffee.in', pageWidth - 12, 26, { align: 'right' });
    
    // Summary box
    let yPos = 40;
    doc.setFillColor(240, 253, 244);
    doc.rect(10, yPos, pageWidth - 20, 14, 'F');
    doc.setDrawColor(5, 150, 105);
    doc.rect(10, yPos, pageWidth - 20, 14, 'S');
    
    doc.setTextColor(6, 95, 70);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${totals.count} Transactions | Receipts: Rs. ${totals.payments.toLocaleString('en-IN')} | Expenses: Rs. ${totals.expenses.toLocaleString('en-IN')} | Net: Rs. ${(totals.payments - totals.expenses).toLocaleString('en-IN')}`, pageWidth / 2, yPos + 9, { align: 'center' });
    
    yPos = 60;
    
    // Table data
    const tableData = filteredTransactions.map((t, idx) => [
      idx + 1,
      formatDate(t.date),
      t.type === 'payment' ? 'Receipt' : 'Expense',
      t.entity_name || '-',
      (t.description || '-').substring(0, 30),
      `Rs. ${t.amount?.toLocaleString('en-IN')}`,
      t.payment_method || '-'
    ]);
    
    // Add totals row
    tableData.push([
      '', 'TOTAL', '', '', '',
      `Rs. ${totals.total.toLocaleString('en-IN')}`,
      ''
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['#', 'Date', 'Type', 'Unit/Vendor', 'Description', 'Amount', 'Method']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9, 
        cellPadding: 3,
        lineColor: [226, 232, 240],
        lineWidth: 0.3
      },
      headStyles: { 
        fillColor: [5, 150, 105], 
        textColor: 255, 
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 20 },
        3: { cellWidth: 30 },
        4: { cellWidth: 50 },
        5: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
        6: { cellWidth: 25 }
      },
      margin: { left: 10, right: 10 },
      didParseCell: (d) => {
        // Total row styling
        if (d.row.index === tableData.length - 1) {
          d.cell.styles.fillColor = [240, 253, 244];
          d.cell.styles.fontStyle = 'bold';
          d.cell.styles.fontSize = 10;
        }
        // Receipt rows green, Expense rows amber
        if (d.column.index === 5 && d.section === 'body' && d.row.index < tableData.length - 1) {
          const txn = filteredTransactions[d.row.index];
          if (txn?.type === 'payment') {
            d.cell.styles.textColor = [5, 150, 105];
          } else {
            d.cell.styles.textColor = [180, 83, 9];
          }
        }
      }
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('All amounts in INR | Generated via FreeCoffee.in', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    
    doc.save(`Transactions_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded');
  };

  // Export to Excel/CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Type', 'Unit/Vendor', 'Description', 'Amount', 'Payment Method', 'Reference', 'Month'];
    const rows = filteredTransactions.map(t => [
      formatDate(t.date),
      t.type === 'payment' ? 'Receipt' : 'Expense',
      t.entity_name || '',
      t.description || '',
      t.amount || 0,
      t.payment_method || '',
      t.reference_id || '',
      t.month_label || ''
    ]);
    
    // Add totals row
    rows.push([]);
    rows.push(['TOTAL', '', '', '', totals.total, '', '', '']);
    rows.push(['Receipts Total', '', '', '', totals.payments, '', '', '']);
    rows.push(['Expenses Total', '', '', '', totals.expenses, '', '', '']);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="transactions-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Transactions</h1>
            <p className="text-sm text-slate-500">View and manage all payment and expense transactions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              <FileSpreadsheet size={14} />Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportToPDF} className="gap-1 border-red-200 text-red-700 hover:bg-red-50">
              <FileDown size={14} />PDF
            </Button>
            <Button variant="outline" size="sm" onClick={loadTransactions} disabled={loading} className="gap-1">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Receipt size={20} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Transactions</p>
                  <p className="text-xl font-bold text-slate-900">{totals.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-100 bg-emerald-50/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CreditCard size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Receipts</p>
                  <p className="text-xl font-bold text-emerald-700">₹{totals.payments.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-100 bg-amber-50/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <IndianRupee size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-amber-600">Expenses</p>
                  <p className="text-xl font-bold text-amber-700">₹{totals.expenses.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <IndianRupee size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-blue-600">Net Balance</p>
                  <p className={`text-xl font-bold ${(totals.payments - totals.expenses) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    ₹{(totals.payments - totals.expenses).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-100 mb-4">
          <CardHeader className="pb-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full lg:w-auto">
                <TabsList className="grid grid-cols-3 w-full lg:w-[280px]">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="payments">Receipts</TabsTrigger>
                  <TabsTrigger value="expenses">Expenses</TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Month Filter */}
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Month:</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="h-9 w-[160px]">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {getUniqueMonths().map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500 whitespace-nowrap">From:</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9 w-[140px]"
                />
                <Label className="text-xs text-slate-500">To:</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9 w-[140px]"
                />
              </div>
              
              {/* Search */}
              <div className="relative flex-1 lg:max-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              
              {/* Clear Filters */}
              {(selectedMonth !== 'all' || startDate || endDate || searchTerm) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-9">
                  Clear Filters
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Table */}
        <Card className="border-slate-100">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-slate-400" size={32} />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <Receipt size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs w-[50px]">#</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Unit/Vendor</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs">Method</TableHead>
                      <TableHead className="text-xs text-center w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((txn, idx) => (
                      <TableRow key={`${txn.type}-${txn.id}`} className="hover:bg-slate-50/50">
                        <TableCell className="text-sm text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="text-sm">{formatDate(txn.date)}</TableCell>
                        <TableCell>
                          <Badge className={txn.type === 'payment' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {txn.type === 'payment' ? 'Receipt' : 'Expense'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{txn.entity_name}</div>
                          {txn.entity_detail && <div className="text-xs text-slate-500">{txn.entity_detail}</div>}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate" title={txn.description}>
                          {txn.description || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${txn.type === 'payment' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            ₹{txn.amount?.toLocaleString('en-IN')}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm capitalize">{txn.payment_method || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => openEditModal(txn)}
                              title="Edit"
                            >
                              <Edit size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteModal({ open: true, transaction: txn })}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-slate-50 font-semibold">
                      <TableCell colSpan={5} className="text-right text-slate-700">TOTAL ({filteredTransactions.length} transactions)</TableCell>
                      <TableCell className="text-right text-emerald-700 text-base">₹{totals.total.toLocaleString('en-IN')}</TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, transaction: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-['Outfit']">
                <Edit size={18} className="text-blue-500" />
                Edit Transaction
              </DialogTitle>
              <DialogDescription>
                {editModal.transaction?.type === 'payment' ? 'Update payment transaction' : 'Update expense payment'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Amount (₹)</Label>
                <Input
                  type="number"
                  value={editData.amount}
                  onChange={(e) => setEditData(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Payment Method</Label>
                <Select value={editData.payment_method} onValueChange={(v) => setEditData(prev => ({ ...prev, payment_method: v }))}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Transaction Date</Label>
                <Input
                  type="date"
                  value={editData.transaction_date}
                  onChange={(e) => setEditData(prev => ({ ...prev, transaction_date: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Reference ID</Label>
                <Input
                  value={editData.reference_id}
                  onChange={(e) => setEditData(prev => ({ ...prev, reference_id: e.target.value }))}
                  placeholder="UPI/Cheque/Transaction reference"
                  className="h-10"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, transaction: null })}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 size={14} className="animate-spin mr-2" />}Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, transaction: null })}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 font-['Outfit']">
                <Trash2 size={18} />
                Delete Transaction
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this transaction? This will also update the related payment record.
              </DialogDescription>
            </DialogHeader>
            {deleteModal.transaction && (
              <div className="py-4 bg-slate-50 rounded-lg px-4">
                <p className="text-sm"><strong>Type:</strong> {deleteModal.transaction.type === 'payment' ? 'Receipt' : 'Expense'}</p>
                <p className="text-sm"><strong>Amount:</strong> ₹{deleteModal.transaction.amount?.toLocaleString('en-IN')}</p>
                <p className="text-sm"><strong>Description:</strong> {deleteModal.transaction.description || '-'}</p>
                <p className="text-sm"><strong>Date:</strong> {formatDate(deleteModal.transaction.date)}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, transaction: null })}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 size={14} className="animate-spin mr-2" />}Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
